package org.ttt.autogenesis.fleetcontrol.protocol

import kotlinx.serialization.Serializable

//========================================
// Fleet readiness contract
//========================================

/**
 * Describes whether the configured AMS fleet can accept a new matchmaking request.
 */
@Serializable
enum class FleetReadinessState
{
    /** The fleet has a usable running server presence. */
    READY,

    /** The fleet is active but is still warming up. */
    STARTING,

    /** The control plane could not determine a safe readiness state. */
    ERROR,
}

/**
 * The two dedicated-server fleets managed by Fleet Control.
 */
@Serializable
enum class FleetRole
{
    /** The long-lived server-extend control-plane fleet. */
    SERVER_EXTEND_FLEET,

    /** The game-server fleet reached through Match2. */
    GAME_SERVER_FLEET,
}

/**
 * The named port returned to a client when a dedicated server is ready.
 */
@Serializable
enum class EndpointPortRole
{
    /** HTTP REST/SSE port used by browser clients. */
    REST,

    /** Internal gRPC port used by service-to-service traffic. */
    GRPC,
}

/**
 * A lease-bound endpoint returned by AMS readiness polling.
 *
 * The identifiers are intentionally part of the public contract: a second
 * browser request must reuse the same AMS session/lease rather than creating
 * another claimed server.
 *
 * @property url Reachable endpoint URL, including scheme and port.
 * @property grpcUrl Reachable native gRPC endpoint in `host:port` form, when
 *   the AMS projection exposes the fleet's named `grpc` port.
 * @property portRole Named port represented by [url].
 * @property dsId AMS dedicated-server identifier.
 * @property amsSessionId AccelByte session used to claim the server.
 * @property leaseId Idempotency/lease identifier owned by Fleet Control.
 */
@Serializable
data class ServerEndpoint(
    val url: String,
    val grpcUrl: String? = null,
    val portRole: EndpointPortRole = EndpointPortRole.REST,
    val dsId: String,
    val amsSessionId: String? = null,
    val leaseId: String? = null,
)

/**
 * Response returned by the public fleet readiness endpoint.
 *
 * @property state Current fleet readiness state.
 * @property retryAfterSeconds Suggested delay before polling again.
 * @property message Sanitized diagnostic text for an error response.
 */
@Serializable
data class EnsureFleetReadyResponse(
    val state: FleetReadinessState,
    val retryAfterSeconds: Int = 0,
    val message: String? = null,
    /** Resolved server-extend endpoint when the requested role is ready. */
    val serverExtendEndpoint: ServerEndpoint? = null,
    /** Role whose readiness was evaluated. */
    val fleetRole: FleetRole = FleetRole.SERVER_EXTEND_FLEET,
)

/**
 * Paths exposed by the Fleet Control HTTP API.
 */
object FleetControlRoutes
{
    /** Public client bootstrap route for the server-extend fleet. */
    const val ENSURE_SERVER_EXTEND_READY: String = "/v1/fleet/ensure-server-extend-ready"

    /** Internal route used by a game server to resolve the leased gRPC endpoint. */
    const val ENSURE_SERVER_EXTEND_ENDPOINT: String = "/v1/internal/ensure-server-extend-endpoint"

    /** Internal route used by server-extend before it enters Match2. */
    const val ENSURE_GAME_SERVER_READY: String = "/v1/internal/ensure-game-server-fleet-ready"

    /** Public route that records demand and ensures fleet readiness. */
    const val ENSURE_READY: String = "/v1/fleet/ensure-ready"

    /** Internal route that records server and server-extend activity. */
    const val ACTIVITY_SIGNAL: String = "/v1/internal/activity-signal"

    /** Internal idempotent fleet/session reconciliation route. */
    const val FLEET_SWEEP: String = "/v1/internal/fleet-sweep"

    /** Server-extend polls this authenticated route for confirmed orphaned Match2 sessions. */
    const val LIST_SESSION_CLEANUPS: String = "/v1/internal/session-cleanups/list"

    /** Server-extend acknowledges cleanup only after AGS confirms the exact session is gone. */
    const val ACK_SESSION_CLEANUP: String = "/v1/internal/session-cleanups/ack"
}

/**
 * Durable request to remove one Match2 session after AMS confirms its exact
 * dedicated server is no longer present in the game-server fleet.
 */
@Serializable
data class OrphanedGameSessionCleanupIntent(
    val intentId: String,
    val role: FleetRole,
    val namespace: String,
    val fleetId: String,
    val dsId: String,
    val amsSessionId: String? = null,
    val gameSessionId: String,
    val detectedAtMillis: Long,
)

/** A scoped acknowledgement for one durable orphan-cleanup intent. */
@Serializable
data class SessionCleanupAckRequest(
    val intentId: String,
    val dsId: String,
    val gameSessionId: String,
)

/** Pending cleanup work returned to the authenticated server-extend service. */
@Serializable
data class SessionCleanupListResponse(
    val intents: List<OrphanedGameSessionCleanupIntent>,
)

//========================================
// Activity signal contract
//========================================

/**
 * Identifies the service that produced an activity signal.
 */
@Serializable
enum class ActivitySourceType
{
    /** Server-extend application telemetry. */
    SERVER_EXTEND,

    /** Dedicated-server application lifecycle telemetry. */
    DEDICATED_SERVER,
}

/**
 * Describes the meaningful application activity observed by a source.
 */
@Serializable
enum class ActivitySignalState
{
    /** The source is registered and currently usable. */
    READY,

    /** The source reports meaningful application activity. */
    ACTIVE,

    /** The source is idle, subject to the coordinator's global shutdown gates. */
    IDLE_CANDIDATE,
}

/**
 * Internal activity telemetry sent by server-extend and dedicated servers.
 *
 * @property sourceType Producing service type.
 * @property sourceId Stable source identifier for the process lifetime.
 * @property state Latest meaningful activity state.
 * @property observedAtMillis Source timestamp for the observation.
 * @property dsId Authoritative AMS dedicated-server identifier when known.
 * @property activeSessionCount Number of active application sessions when known.
 * @property hasRealClientActivity Whether meaningful client activity exists.
 * @property lastRealClientActivityAtMillis Last meaningful client activity timestamp.
 * @property hasServerActivity Whether meaningful dedicated-server activity exists.
 * @property lastServerActivityAtMillis Last meaningful server activity timestamp.
 * @property isFullyIdleForRealActivity Whether the complete application snapshot is idle.
 * @property sessionId AccelByte session associated with the observation, when known.
 * @property terminationReason Application-specific reason for an idle candidate.
 */
@Serializable
data class ActivitySignalRequest(
    val sourceType: ActivitySourceType,
    val sourceId: String,
    val state: ActivitySignalState,
    val observedAtMillis: Long,
    val fleetRole: FleetRole = FleetRole.GAME_SERVER_FLEET,
    val dsId: String? = null,
    val amsSessionId: String? = null,
    val leaseId: String? = null,
    val activeSessionCount: Int? = null,
    val activeDurableSessionCount: Int? = null,
    val hasRealClientActivity: Boolean? = null,
    val lastRealClientActivityAtMillis: Long? = null,
    val hasServerActivity: Boolean? = null,
    val lastServerActivityAtMillis: Long? = null,
    val isFullyIdleForRealActivity: Boolean? = null,
    val sessionId: String? = null,
    val meaningfulActivity: Boolean? = null,
    val terminationReason: String? = null,
)

/**
 * Scoped request for the idempotent sweep operation.
 *
 * @property fleetRoles Roles that may be inspected. The empty set is invalid
 *   for a real request and prevents accidental tenant-wide sweeps.
 * @property nowMillis Controller time used for deterministic local rehearsals.
 */
@Serializable
data class FleetSweepRequest(
    val fleetRoles: Set<FleetRole>,
    val nowMillis: Long? = null,
)
